import * as React from "react";
import { Award, RefreshCw } from "lucide-react";
import {
  Button,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTablePagination,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from "@eduviaos/ui-kit";
import { usePageSizePreference } from "@/hooks/usePageSizePreference";
import { useAdminSkillCertificates } from "@/lib/learning/adminApi";

function formatWhen(iso: string): string {
  return new Intl.DateTimeFormat("en-IN", { dateStyle: "medium" }).format(new Date(iso));
}

export function LearningCertificatesTab() {
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = usePageSizePreference({
    scope: "learning-certificates",
    defaultPageSize: 25,
  });
  const query = useAdminSkillCertificates({ limit: pageSize, offset: page * pageSize });
  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  return (
    <DataTable>
      <DataTableToolbar>
        <DataTableToolbarSpacer />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => query.refetch()}
          disabled={query.isFetching}
        >
          <RefreshCw
            aria-hidden="true"
            className={cn("size-4", query.isFetching && "animate-spin")}
          />
          Refresh
        </Button>
        <span className="text-muted-foreground text-xs tabular-nums">
          {total.toLocaleString()} certificates
        </span>
      </DataTableToolbar>
      <DataTableScroll>
        <Table>
          <caption className="sr-only">Skill certificates issued to learners</caption>
          <TableHeader>
            <TableRow>
              <TableHead>Learner</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Topic</TableHead>
              <TableHead className="text-right">Score</TableHead>
              <TableHead>Issued</TableHead>
              <TableHead>Verify code</TableHead>
            </TableRow>
          </TableHeader>
          {query.isLoading ? (
            <DataTableSkeleton rows={8} columns={6} />
          ) : (
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-medium">
                    {row.learner_display_name ?? "Learner"}
                  </TableCell>
                  <TableCell>{row.subject}</TableCell>
                  <TableCell>{row.topic}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {Math.round(row.score * 100)}%
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {formatWhen(row.issued_at)}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{row.verify_code}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
      </DataTableScroll>
      {!query.isLoading && items.length === 0 ? (
        <DataTableEmpty
          icon={<Award aria-hidden="true" />}
          title="No certificates yet"
          description="Certificates appear here when learners pass skill assessments."
        />
      ) : null}
      {!query.isLoading && items.length > 0 ? (
        <DataTableFooter>
          <DataTablePagination
            page={page + 1}
            pageSize={pageSize}
            totalRows={total}
            onPageChange={(next) => setPage(Math.max(0, next - 1))}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(0);
            }}
            testIdPrefix="learning-certificates"
          />
        </DataTableFooter>
      ) : null}
    </DataTable>
  );
}
